import type {
  User,
  Student,
  Activity,
  DailyReport,
  Pagination,
  Camera,
  Detection,
  Area,
  School,
  Notification,
} from '../types/api';

// Type guard validation options
export interface TypeGuardOptions {
  throwOnError?: boolean;
  logErrors?: boolean;
}

// UUID validation regex
const UUID_REGEX = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;

// Helper function to check if value is a valid UUID
function isUUID(value: unknown): boolean {
  return typeof value === 'string' && UUID_REGEX.test(value);
}

// User type guard
export function isUser(value: unknown): value is User {
  if (!value || typeof value !== 'object') return false;
  
  const obj = value as any;
  return (
    isUUID(obj.id) &&
    typeof obj.email === 'string' &&
    obj.email.includes('@') &&
    typeof obj.first_name === 'string' &&
    typeof obj.last_name === 'string' &&
    typeof obj.full_name === 'string' &&
    ['viewer', 'operator', 'admin', 'super_admin', 'teacher'].includes(obj.role) &&
    typeof obj.active === 'boolean' &&
    typeof obj.created_at === 'string' &&
    typeof obj.updated_at === 'string'
  );
}

// Student type guard
export function isStudent(value: unknown): value is Student {
  if (!value || typeof value !== 'object') return false;
  
  const obj = value as any;
  return (
    isUUID(obj.id) &&
    typeof obj.first_name === 'string' &&
    typeof obj.last_name === 'string' &&
    typeof obj.date_of_birth === 'string' &&
    ['male', 'female', 'other', 'prefer_not_to_say'].includes(obj.gender) &&
    ['enrolled', 'pending', 'graduated', 'transferred', 'withdrawn', 'suspended'].includes(obj.enrollment_status) &&
    typeof obj.active === 'boolean' &&
    typeof obj.created_at === 'string' &&
    typeof obj.updated_at === 'string'
  );
}

// Camera type guard
export function isCamera(value: unknown): value is Camera {
  if (!value || typeof value !== 'object') return false;
  
  const obj = value as any;
  return (
    isUUID(obj.id) &&
    isUUID(obj.area_id) &&
    isUUID(obj.school_id) &&
    typeof obj.name === 'string' &&
    typeof obj.rtsp_url === 'string' &&
    ['active', 'inactive', 'maintenance', 'error'].includes(obj.status) &&
    ['healthy', 'warning', 'error', 'offline', 'unknown'].includes(obj.health_status) &&
    typeof obj.active === 'boolean' &&
    typeof obj.created_at === 'string' &&
    typeof obj.updated_at === 'string'
  );
}

// Activity type guard
export function isActivity(value: unknown): value is Activity {
  if (!value || typeof value !== 'object') return false;
  
  const obj = value as any;
  return (
    isUUID(obj.id) &&
    isUUID(obj.student_id) &&
    isUUID(obj.area_id) &&
    typeof obj.activity_type === 'string' &&
    typeof obj.start_time === 'string' &&
    typeof obj.safety_score === 'number' &&
    typeof obj.verified === 'boolean' &&
    Array.isArray(obj.tags) &&
    typeof obj.created_at === 'string' &&
    typeof obj.updated_at === 'string'
  );
}

// Daily Report type guard
export function isDailyReport(value: unknown): value is DailyReport {
  if (!value || typeof value !== 'object') return false;
  
  const obj = value as any;
  return (
    isUUID(obj.id) &&
    isUUID(obj.student_id) &&
    typeof obj.date === 'string' &&
    ['happy', 'sad', 'neutral', 'excited', 'tired', 'upset'].includes(obj.mood) &&
    typeof obj.meals === 'object' &&
    typeof obj.nap === 'object' &&
    Array.isArray(obj.activities) &&
    typeof obj.notes === 'string' &&
    ['draft', 'completed', 'sent'].includes(obj.status) &&
    typeof obj.created_at === 'string' &&
    typeof obj.updated_at === 'string'
  );
}

// Pagination type guard
export function isPagination(value: unknown): value is Pagination {
  if (!value || typeof value !== 'object') return false;
  
  const obj = value as any;
  return (
    typeof obj.current_page === 'number' &&
    typeof obj.total_pages === 'number' &&
    typeof obj.total_count === 'number' &&
    typeof obj.per_page === 'number' &&
    (obj.has_previous === undefined || typeof obj.has_previous === 'boolean') &&
    (obj.has_next === undefined || typeof obj.has_next === 'boolean')
  );
}

// Detection type guard
export function isDetection(value: unknown): value is Detection {
  if (!value || typeof value !== 'object') return false;
  
  const obj = value as any;
  return (
    isUUID(obj.id) &&
    isUUID(obj.camera_id) &&
    ['face', 'person', 'object', 'action', 'emotion', 'pose'].includes(obj.detection_type) &&
    typeof obj.confidence === 'number' &&
    typeof obj.detected_at === 'string' &&
    typeof obj.verified === 'boolean' &&
    typeof obj.created_at === 'string'
  );
}

// Area type guard
export function isArea(value: unknown): value is Area {
  if (!value || typeof value !== 'object') return false;
  
  const obj = value as any;
  return (
    isUUID(obj.id) &&
    isUUID(obj.school_id) &&
    typeof obj.name === 'string' &&
    typeof obj.area_type === 'string' &&
    typeof obj.active === 'boolean' &&
    typeof obj.created_at === 'string' &&
    typeof obj.updated_at === 'string'
  );
}

// School type guard
export function isSchool(value: unknown): value is School {
  if (!value || typeof value !== 'object') return false;
  
  const obj = value as any;
  return (
    isUUID(obj.id) &&
    typeof obj.name === 'string' &&
    typeof obj.active === 'boolean' &&
    typeof obj.created_at === 'string' &&
    typeof obj.updated_at === 'string'
  );
}

// Notification type guard
export function isNotification(value: unknown): value is Notification {
  if (!value || typeof value !== 'object') return false;
  
  const obj = value as any;
  return (
    isUUID(obj.id) &&
    isUUID(obj.user_id) &&
    typeof obj.notification_type === 'string' &&
    typeof obj.title === 'string' &&
    typeof obj.message === 'string' &&
    ['low', 'medium', 'high', 'critical'].includes(obj.priority) &&
    typeof obj.created_at === 'string'
  );
}

// Array type guards
export function isUserArray(value: unknown): value is User[] {
  return Array.isArray(value) && value.every(isUser);
}

export function isStudentArray(value: unknown): value is Student[] {
  return Array.isArray(value) && value.every(isStudent);
}

export function isCameraArray(value: unknown): value is Camera[] {
  return Array.isArray(value) && value.every(isCamera);
}

export function isActivityArray(value: unknown): value is Activity[] {
  return Array.isArray(value) && value.every(isActivity);
}

// Generic validation helper
export function validateApiResponse<T>(
  value: unknown,
  guard: (value: unknown) => value is T,
  options: TypeGuardOptions = {}
): T | undefined {
  if (guard(value)) {
    return value;
  }

  const error = new Error('Type validation failed');
  
  if (options.logErrors) {
    console.error('Type guard validation failed:', {
      value,
      guard: guard.name,
      error: error.message,
    });
  }

  if (options.throwOnError) {
    throw error;
  }

  return undefined;
}